import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { toast } from "react-toastify";
import { EntregadorNaFila, EntregadorEmEntrega, PedidoExternoDetalhe } from "../types";
import {
  useRankingDiaEntrega,
  useCreateEntrega,
  useUpdateFilaEntregaStatus,
} from "../../../hooks/useRoterista";
import { useLojaById } from "../../../hooks/useLojas";
import { filaEntregaService } from "../../../services/filaEntregaService";
import { aguardandoEntregaService } from "../../../services/aguardandoEntregaService";
import { useEntregas, useUpdateEntrega, useFinalizarEntrega } from "../../../hooks/useEntrega";
import { useRankingEvents } from "../../../hooks/useRankingEvents";
import { RANKING_SSE_QUERY_KEYS } from "../../../constants/rankingEventQueryKeys";
import { useAuth } from "../../../context/AuthContext";
import { canVerTodasLojasUser } from "../../../lib/screenAccess";
import { BrazilDate } from "../../../utils/BrazilDate";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { lojaService } from "../../../services/lojaService";
import { Loja } from "../../../types/loja";

interface RoteristaContextType {
  lojaId: number | undefined;
  lojas: Loja[];
  selectedLojaId: number | undefined;
  setSelectedLojaId: (id: number | undefined) => void;
  showLojaSelector: boolean;
  isLoading: boolean;
  entregadoresNaFila: EntregadorNaFila[];
  entregadoresEmEntrega: EntregadorEmEntrega[];
  proximoChamado: EntregadorNaFila | null;
  enderecos: number | "";
  stats: {
    totalAguardando: number;
    totalEmEntrega: number;
    totalEntregadores: number;
    proximoNaFila: EntregadorNaFila | null;
  };
  setProximoChamado: (entregador: EntregadorNaFila | null) => void;
  setEnderecos: (value: number | "") => void;
  isChamando: boolean;
  isConfirmandoSaida: boolean;
  handleChamarProximo: () => void;
  handleChamarEntregador: (entregadorId: number, nome: string) => void;
  handleConfirmarSaida: (
    displayIds: number[],
    entregaExterna?: boolean,
    pedidosExternosDetalhes?: PedidoExternoDetalhe[]
  ) => Promise<void>;
  handleMoverParaFim: () => Promise<void>;
  handleChamarNovamente: () => Promise<void>;
  handleCloseModal: () => void;
  handleFinalizarDia: (filaId: number) => Promise<void>;
  isFinalizandoDia: boolean;
  usaIntegracaoPedidos: boolean;
  atualizarPedidosEntrega: (
    entregaId: number,
    pedidosDisplayIds: number[]
  ) => Promise<void>;
  handleFinalizarEntrega: (entregaId: number) => Promise<void>;
  finalizandoEntregaId: number | null;
  numerosPermitidosNaFila: Set<number>;
  entregaSseAvailable: boolean;
}

const RoteristaContext = createContext<RoteristaContextType | undefined>(
  undefined
);

export const RoteristaProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const podeVerTodasLojas = canVerTodasLojasUser(user);
  const [selectedLojaId, setSelectedLojaId] = useState<number | undefined>(
    () => user?.lojaId
  );

  const effectiveLojaId = podeVerTodasLojas
    ? (selectedLojaId ?? user?.lojaId)
    : user?.lojaId;
  const lojaId = effectiveLojaId;

  const { data: lojas = [] } = useQuery({
    queryKey: ["lojas"],
    queryFn: () => lojaService.getLojas(),
  });

  const [proximoChamado, setProximoChamado] = useState<EntregadorNaFila | null>(
    null
  );
  const [enderecos, setEnderecos] = useState<number | "">("");
  const [isChamando, setIsChamando] = useState(false);
  const [isConfirmandoSaida, setIsConfirmandoSaida] = useState(false);
  const confirmandoSaidaRef = useRef(false);
  const [finalizandoEntregaId, setFinalizandoEntregaId] = useState<
    number | null
  >(null);
  const finalizandoEntregaRef = useRef(false);

  const { data: rankingData, isLoading: isLoadingRanking } =
    useRankingDiaEntrega({
      lojaId: lojaId || undefined,
    });

  const { data: entregasData, isLoading: isLoadingEntregas } = useEntregas({
    lojaId: lojaId || undefined,
    status: "ativo",
  });

  const { data: lojaAtual } = useLojaById(lojaId);

  const createEntregaMutation = useCreateEntrega();
  const updateFilaMutation = useUpdateFilaEntregaStatus();
  const updateEntregaMutation = useUpdateEntrega();
  const finalizarEntregaMutation = useFinalizarEntrega();

  const [liveUpdatesEnabled, setLiveUpdatesEnabled] = useState(false);
  const [entregaSseAvailable, setEntregaSseAvailable] = useState(true);

  useEffect(() => {
    const timer = window.setTimeout(() => setLiveUpdatesEnabled(true), 1500);
    return () => window.clearTimeout(timer);
  }, []);

  const invalidateRoteristaLiveData = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["aguardando-entrega"] });
    void queryClient.invalidateQueries({ queryKey: ["rankingDiaEntrega"] });
    void queryClient.invalidateQueries({ queryKey: ["entregas"] });
  }, [queryClient]);

  useRankingEvents("entrega", lojaId || null, {
    queryKeys: RANKING_SSE_QUERY_KEYS.roterista,
    enabled: liveUpdatesEnabled,
    onAvailabilityChange: setEntregaSseAvailable,
  });

  useEffect(() => {
    if (entregaSseAvailable) return;

    invalidateRoteristaLiveData();
    const interval = window.setInterval(invalidateRoteristaLiveData, 30_000);
    return () => window.clearInterval(interval);
  }, [entregaSseAvailable, invalidateRoteristaLiveData]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        invalidateRoteristaLiveData();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [invalidateRoteristaLiveData]);

  const rankingPorEntregador = useMemo(() => {
    const map = new Map<number, number>();
    rankingData?.ranking?.forEach((r) => {
      map.set(r.usuarioId, r.quantidadeEntregas);
    });
    return map;
  }, [rankingData?.ranking]);

  const entregadoresNaFila = useMemo(() => {
    if (!rankingData?.aguardando) return [];

    return rankingData.aguardando
      .map((item, index) => {
        const nomeCompleto = item.nome.split(" ");
        const nome = nomeCompleto[0] || "";
        const sobrenome = nomeCompleto.slice(1).join(" ") || "";

        return {
          id: item.id,
          entregadorId: item.entregadorId,
          nome,
          sobrenome,
          posicao: index + 1,
          filaId: item.id,
          dataEntrada:
            typeof item.dataEntrada === "string"
              ? item.dataEntrada
              : new Date(item.dataEntrada).toISOString(),
          quantidadeEntregasFinalizadas:
            rankingPorEntregador.get(item.entregadorId) ?? 0,
        };
      })
      .sort(
        (a, b) =>
          new Date(a.dataEntrada).getTime() - new Date(b.dataEntrada).getTime()
      )
      .map((item, index) => ({
        ...item,
        posicao: index + 1,
      }));
  }, [rankingData, rankingPorEntregador]);

  const entregadoresEmEntrega = useMemo(() => {
    const emEntrega: EntregadorEmEntrega[] = [];
    const entregadoresUnicos = new Set<number>();

    if (entregasData?.data) {
      entregasData.data.forEach((entrega) => {
        if (
          entrega.status === "ativo" &&
          !entrega.dataFim &&
          entrega.entregador &&
          !entregadoresUnicos.has(entrega.entregador.id)
        ) {
          entregadoresUnicos.add(entrega.entregador.id);
          const nomeCompleto = entrega.entregador.nome.split(" ");
          emEntrega.push({
            id: entrega.id,
            entregadorId: entrega.entregador.id,
            nome: nomeCompleto[0] || "",
            sobrenome: nomeCompleto.slice(1).join(" ") || "",
            quantidadeEntregasFinalizadas:
              rankingPorEntregador.get(entrega.entregador.id) ?? 0,
            quantidadeEnderecos: entrega.quantidadeEnderecos ?? 1,
            pedidosDisplayIds: entrega.pedidosDisplayIds ?? undefined,
          });
        }
      });
    }

    return emEntrega;
  }, [entregasData, rankingPorEntregador]);

  const stats = useMemo(() => {
    const totalAguardando = rankingData?.entregadoresAguardando || 0;
    const totalEmEntrega = rankingData?.quantidadeEmAndamento || 0;
    const proximoNaFila = entregadoresNaFila[0] || null;

    return {
      totalAguardando,
      totalEmEntrega,
      totalEntregadores: totalAguardando + totalEmEntrega,
      proximoNaFila,
    };
  }, [rankingData, entregadoresNaFila]);

  const isLoading = isLoadingRanking || isLoadingEntregas;

  const handleChamarProximo = async () => {
    if (entregadoresNaFila.length === 0) return;
    const proximo = entregadoresNaFila[0];
    const filaId = proximo.filaId ?? proximo.id;
    if (filaId == null || filaId === undefined) {
      toast.error("Erro: ID da fila não encontrado.");
      return;
    }
    setIsChamando(true);
    try {
      await filaEntregaService.chamarEntregador(filaId);
      setProximoChamado(proximo);
    } catch (error) {
      console.error("Erro ao emitir chamada para ranking/entregas:", error);
      toast.error(
        "Não foi possível emitir o alerta na tela de entregas. Tente novamente."
      );
    } finally {
      setIsChamando(false);
    }
  };

  const handleChamarEntregador = async (entregadorId: number) => {
    const entregador = entregadoresNaFila.find(
      (e) => e.entregadorId === entregadorId
    );
    if (!entregador) return;
    const filaId = entregador.filaId ?? entregador.id;
    setIsChamando(true);
    try {
      await filaEntregaService.chamarEntregador(filaId);
      setProximoChamado(entregador);
    } catch (error) {
      console.error("Erro ao emitir chamada para ranking/entregas:", error);
      toast.error(
        "Não foi possível emitir o alerta na tela de entregas. Tente novamente."
      );
    } finally {
      setIsChamando(false);
    }
  };

  const usaIntegracaoPedidos =
    lojaAtual?.usaIntegracaoPedidos ??
    user?.loja?.usaIntegracaoPedidos ??
    false;

  const { data: aguardandoModalData } = useQuery({
    queryKey: ["aguardando-entrega", lojaId, "pending"],
    queryFn: () =>
      aguardandoEntregaService.listar(lojaId!, undefined, undefined, "pending"),
    enabled: Boolean(lojaId && proximoChamado && usaIntegracaoPedidos),
  });

  const numerosPermitidosNaFila = useMemo(() => {
    const s = new Set<number>();
    for (const i of aguardandoModalData?.items ?? []) {
      if (!i.prontoParaEntrega) continue;
      s.add(i.displayId);
      s.add(i.orderId);
    }
    return s;
  }, [aguardandoModalData?.items]);

  const handleConfirmarSaida = async (
    displayIds: number[],
    entregaExterna = false,
    pedidosExternosDetalhes?: PedidoExternoDetalhe[]
  ) => {
    if (confirmandoSaidaRef.current || isConfirmandoSaida) {
      return;
    }

    if (
      usaIntegracaoPedidos &&
      (!displayIds?.length || displayIds.length !== enderecos)
    ) {
      toast.error("Informe os números dos pedidos para cada endereço.");
      return;
    }

    if (!enderecos || enderecos < 1 || enderecos > 3) {
      toast.error("Quantidade de endereços deve ser entre 1 e 3!");
      return;
    }

    if (!proximoChamado || !lojaId) {
      toast.error("Erro ao confirmar saída");
      return;
    }

    confirmandoSaidaRef.current = true;
    setIsConfirmandoSaida(true);

    try {
      await createEntregaMutation.mutateAsync({
        lojaId,
        entregadorId: proximoChamado.entregadorId,
        quantidadeEnderecos: enderecos,
        ...(usaIntegracaoPedidos &&
          displayIds?.length && { pedidosDisplayIds: displayIds }),
        ...(usaIntegracaoPedidos && entregaExterna && { entregaExterna: true }),
        ...(usaIntegracaoPedidos &&
          entregaExterna &&
          pedidosExternosDetalhes?.length && {
            pedidosExternosDetalhes,
          }),
      });

      await updateFilaMutation.mutateAsync({
        id: proximoChamado.filaId,
        data: { status: "em_andamento" },
      });

      toast.success("Saída confirmada!");
      setProximoChamado(null);
      setEnderecos("");
    } catch (error) {
      console.error("Erro ao confirmar saída:", error);
      toast.error(
        error instanceof Error ? error.message : "Erro ao confirmar saída"
      );
    } finally {
      confirmandoSaidaRef.current = false;
      setIsConfirmandoSaida(false);
    }
  };

  const handleMoverParaFim = async () => {
    if (!proximoChamado) return;

    try {
      await updateFilaMutation.mutateAsync({
        id: proximoChamado.filaId,
        data: {
          status: "aguardando",
          dataFila: BrazilDate.now().toISOString(),
        },
      });
      toast.success("Entregador movido para o fim da fila");
      setProximoChamado(null);
    } catch (error) {
      console.error("Erro ao mover entregador:", error);
      toast.error("Erro ao mover entregador");
    }
  };

  const handleChamarNovamente = async () => {
    if (!proximoChamado) return;
    const filaId = proximoChamado.filaId ?? proximoChamado.id;
    if (filaId == null || filaId === undefined) return;
    try {
      await filaEntregaService.chamarEntregador(filaId);
      toast.success("Chamada emitida novamente na tela de entregas.");
    } catch (error) {
      console.error("Erro ao emitir chamada novamente:", error);
      toast.error(
        "Não foi possível emitir o alerta novamente. Tente novamente."
      );
    }
  };

  const handleCloseModal = () => {
    setProximoChamado(null);
    setEnderecos("");
  };

  const handleFinalizarDia = async (filaId: number) => {
    try {
      await updateFilaMutation.mutateAsync({
        id: filaId,
        data: { status: "finalizado" },
      });
      toast.success("Dia finalizado com sucesso!");
    } catch (error) {
      console.error("Erro ao finalizar dia:", error);
      toast.error("Erro ao finalizar dia do entregador");
    }
  };

  const atualizarPedidosEntrega = async (
    entregaId: number,
    pedidosDisplayIds: number[]
  ) => {
    await updateEntregaMutation.mutateAsync({
      id: entregaId,
      data: {
        pedidosDisplayIds,
        quantidadeEnderecos:
          pedidosDisplayIds.length > 0 ? pedidosDisplayIds.length : 1,
      },
    });
    toast.success("Números do pedido atualizados.");
  };

  const handleFinalizarEntrega = async (entregaId: number) => {
    if (finalizandoEntregaRef.current || finalizandoEntregaId != null) return;

    finalizandoEntregaRef.current = true;
    setFinalizandoEntregaId(entregaId);
    try {
      await finalizarEntregaMutation.mutateAsync(entregaId);
      invalidateRoteristaLiveData();
      toast.success(
        "Entrega finalizada! Entregador volta para a fila aguardando."
      );
    } catch (error) {
      const message =
        error instanceof Error ? error.message : String(error);
      if (message.includes("já foi finalizada")) {
        invalidateRoteristaLiveData();
        toast.success("Entrega já estava finalizada.");
      } else {
        console.error(error);
        toast.error(
          "Erro ao finalizar entrega: " +
            (error instanceof Error ? error.message : "Erro desconhecido")
        );
      }
    } finally {
      finalizandoEntregaRef.current = false;
      setFinalizandoEntregaId(null);
    }
  };

  if (!podeVerTodasLojas && !lojaId) {
    return (
      <div className="flex h-screen items-center justify-center">
        <p className="text-neutral-500">Erro: Loja não identificada</p>
      </div>
    );
  }

  return (
    <RoteristaContext.Provider
      value={{
        lojaId,
        lojas,
        selectedLojaId,
        setSelectedLojaId,
        showLojaSelector: podeVerTodasLojas,
        isLoading,
        entregadoresNaFila,
        entregadoresEmEntrega,
        proximoChamado,
        enderecos,
        stats,
        setProximoChamado,
        setEnderecos,
        isChamando,
        isConfirmandoSaida,
        handleChamarProximo,
        handleChamarEntregador,
        handleConfirmarSaida,
        handleMoverParaFim,
        handleChamarNovamente,
        handleCloseModal,
        handleFinalizarDia,
        isFinalizandoDia: updateFilaMutation.isPending,
        usaIntegracaoPedidos,
        atualizarPedidosEntrega,
        handleFinalizarEntrega,
        finalizandoEntregaId,
        numerosPermitidosNaFila,
        entregaSseAvailable,
      }}
    >
      {children}
    </RoteristaContext.Provider>
  );
};

export const useRoterista = () => {
  const context = useContext(RoteristaContext);
  if (!context) {
    throw new Error("useRoterista must be used within RoteristaProvider");
  }
  return context;
};
